import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { BaseApiService, PageableResponsePayloadModel } from '@eagle/shared';

import { ConfigurationAPISetting } from '../../../api';
import {
  CompanyMatchingListItemModel,
  CompanyMatchingResultListItemModel,
  EmployeeMatchingResultListItemModel,
  EmployeeMatchingResultModel,
  VacancyMatchingListItemModel,
} from '../models';

@Injectable({
  providedIn: 'root',
})
export class MatchingApiService {
  private baseApiService = inject(BaseApiService);

  private readonly endpoint = `${ConfigurationAPISetting.endpoint}/matching`;

  getEmployeeMatchingList(
    id: string,
    params = {}
  ): Observable<PageableResponsePayloadModel<CompanyMatchingResultListItemModel[]>> {
    return this.baseApiService.get(`${this.endpoint}/employees/${id}`, { ...params });
  }

  getVacancyMatchingList(
    id: string,
    params = {}
  ): Observable<PageableResponsePayloadModel<EmployeeMatchingResultListItemModel[]>> {
    return this.baseApiService.get(`${this.endpoint}/vacancies/${id}`, { ...params });
  }

  getPseudoVacancyMatchingList(
    companyId: string,
    pseudoVacancyId: string,
    params = {}
  ): Observable<PageableResponsePayloadModel<EmployeeMatchingResultListItemModel[]>> {
    return this.baseApiService.get(
      `${this.endpoint}/companies/${companyId}/infoCompetences/${pseudoVacancyId}`,
      { ...params }
    );
  }

  getCompanyMatchingList(
    id: string,
    params = {}
  ): Observable<PageableResponsePayloadModel<EmployeeMatchingResultListItemModel[]>> {
    return this.baseApiService.get(`${this.endpoint}/companies/${id}`, { ...params });
  }

  getMatchingResult(
    matchingId: string,
    companyId: string
  ): Observable<EmployeeMatchingResultModel> {
    const url = [this.endpoint, matchingId, 'company', companyId].join('/');

    return this.baseApiService.get(url);
  }

  getCompanyMatchingListById(matchingId: string): Observable<CompanyMatchingResultListItemModel[]> {
    return this.baseApiService.get(`${this.endpoint}/${matchingId}/company`);
  }

  getEmployeeMatchingListById(
    matchingId: string
  ): Observable<EmployeeMatchingResultListItemModel[]> {
    return this.baseApiService.get(`${this.endpoint}/${matchingId}/employee`, undefined, false);
  }

  getVacancyMatchingListById(
    matchingId: string
  ): Observable<EmployeeMatchingResultListItemModel[]> {
    return this.baseApiService.get(`${this.endpoint}/${matchingId}/vacancy`, undefined, false);
  }

  getEmployeeInternalVacancies(
    employeeId: string,
    params = {}
  ): Observable<VacancyMatchingListItemModel[]> {
    return this.baseApiService.get(`${this.endpoint}/employees/${employeeId}/vacancies`, params);
  }

  getEmployeeExternalVacancies(
    employeeId: string,
    params = {}
  ): Observable<VacancyMatchingListItemModel[]> {
    return this.baseApiService.get(
      `${this.endpoint}/employees/${employeeId}/externalVacancies`,
      params
    );
  }

  getEmployeeMatchingCompanies(
    employeeId: string,
    params = {}
  ): Observable<CompanyMatchingListItemModel[]> {
    return this.baseApiService.get(`${this.endpoint}/employees/${employeeId}/companies`, params);
  }
}
